import React, { useEffect, useState } from "react";
import Helmet from "react-helmet";
import { injectIntl } from "react-intl";
import { Spin } from "antd";
import _ from "lodash";
import { inject, observer } from "mobx-react";
import { HistoryInfoSection, NeighborhoodInfoSection, OtherInfoSection, PropertyInfoSection } from "./Sections";
import MapView from "./Views/MapView";
import NotFound from "containers/NotFound";
import { formatSearchParams } from "utils/helpers/searchHelper";
import { getFullAddress } from "utils/helpers/housesHelper/getter";
import { HouseAllPhotosView, TopBarView } from "./Views";
import "./styles.scss";


const PAGE_TOP_MARGIN_IN_PIXELS = 30;

const HouseDetails = observer(({ housesStore, criteriaStore, propertyId }) => {
  const [isPhotoCollageOpened, setIsPhotoCollageOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isTopBarFixed, setIsTopBarFixed] = useState(false);
  const [screenSize, setScreenSize] = useState({ height: window.innerHeight, width: window.innerWidth });
  const houseDetails = housesStore.retrieveHouseDetailsData();

  useEffect(() => {
    const handleSuccess = () => {
      setIsLoading(false);
    }
    const handleError = () => {
      setIsLoading(false);
    }
    const criteriaData = criteriaStore.retrieveCriteriaData();
    const searchFilters = formatSearchParams(criteriaData);

    if (!_.isEmpty(searchFilters)) {
      housesStore.getHouseDetailsRequest({
        filters: searchFilters,
        id: propertyId,
      }, handleSuccess, handleError);
    } else {
      const handleCriteriaSuccess = data => {
        const searchFilters = formatSearchParams(data);
        housesStore.getHouseDetailsRequest({
          filters: searchFilters,
          id: propertyId,
        }, handleSuccess, handleError);
      }
      criteriaStore.getCriteriaRequest(null, handleCriteriaSuccess);
    }

    return () => housesStore.setHouseDetails({});
  }, []);

  const handleResize = () => setScreenSize({ height: window.innerHeight, width: window.innerWidth });

  const handleScroll = () => setIsTopBarFixed(window.scrollY > PAGE_TOP_MARGIN_IN_PIXELS);

  useEffect(() => {
    // tracks scroll event to fix main house info at the top of the page
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    }
  }, [handleResize, handleScroll]);

  const getPropertyInfoSection = () => (
    <PropertyInfoSection
      houseDetails={houseDetails}
      onAllPhotosButtonClick={() => setIsPhotoCollageOpened(true)}
    />
  );

  const getHistoryInfoSection = () => <HistoryInfoSection houseDetails={houseDetails} />;

  const getMapAndNeighborhoodInfoSection = () => (
    <>
      <MapView houseDetails={houseDetails} />
      <NeighborhoodInfoSection houseDetails={houseDetails} />
    </>
  );

  const getOtherInfoSection = () => <OtherInfoSection houseDetails={houseDetails} />;
  // const getOtherInfoSection = () => renderDetails(getOtherDetailsRow, OTHER_DETAILS, "houseDetails.otherDetailsTitle");

  const getInfoBlock = index => {
    const orderedInfoBlockMethods = [
      getPropertyInfoSection,
      getMapAndNeighborhoodInfoSection,
      getOtherInfoSection,
      getHistoryInfoSection
    ];
    return index < orderedInfoBlockMethods.length ? orderedInfoBlockMethods[index]() : null;
  };

  // const renderDetails = (method, data, messageId) => (
  //   <div className="details-collapse">
  //     <Collapse bordered={false} expandIconPosition="right">
  //       <Panel header={intl.formatMessage({ id: messageId })} key="1">
  //         <div className="other-details-wrapper">
  //           {_.map(data, (item, key) => method(houseDetails, item, key))}
  //         </div>
  //       </Panel>
  //     </Collapse>
  //   </div>
  // );

  const renderHouse = () => (
    <div className="house-details-container">
      <TopBarView
        isFixed={isTopBarFixed}
        isPhotoCollageOpened={isPhotoCollageOpened}
        houseDetails={houseDetails}
        housesStore={housesStore}
        onReturnButtonClick={() => setIsPhotoCollageOpened(false)}
      />
      {isPhotoCollageOpened ? renderHousePhotos() : renderHouseDetails()}
    </div>
  );

  const renderHouseDetails = () => {
    const houseInfoBlocks = _.range(4);
    return (
    <>
      {houseInfoBlocks.map(index => (
        <div className="house-info-block">
          {getInfoBlock(index)}
          {index !== houseInfoBlocks.length - 1 && <div className="separator-line" />}
        </div>
      ))}
    </>
    );
  };

  const renderHousePhotos = () => (
    <div><HouseAllPhotosView houseDetails={houseDetails} screenSize={screenSize} /></div>
  );

  if (_.isEmpty(houseDetails) && !isLoading) {
    return <NotFound customClassName="not-found-page" width="85vw" height="90vh" />;
  }

  return (
    <>
      <Helmet title={!_.isEmpty(houseDetails) && !isLoading && getFullAddress(houseDetails.address)} />
      <div className="house-details-wrapper">
        <Spin spinning={isLoading}>
          {!_.isEmpty(houseDetails) && !isLoading && renderHouse()}
        </Spin>
      </div>
    </>
  );
});

export default inject("housesStore", "criteriaStore")(injectIntl(HouseDetails));
